/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

// THE BENEFICIAL OWNERSHIP THRESHOLD (v1_830). One number, set by the firm
// (policy questionnaire section 3, written to risk_config.uboThreshold on
// signing), above 0 and no more than 25, default 25. A holder AT OR ABOVE it is
// a beneficial owner: on the UBO list, verified, and on a High case flagged for
// a personal source of wealth interview (section 9 uses the same number).
//
// The engine reads the firm setting at each rebuild and retier and stamps it on
// the case (report.ownershipThreshold); every screen and export reads the
// case's stamp, so a case shows the threshold it was built under until its next
// rebuild. Swiss VQF Form K keeps its statutory 25 percent box whatever the
// firm sets (it is the form's own definition, not the firm's policy).

export const DEFAULT_OWNERSHIP_THRESHOLD = 25;
export const FORM_K_THRESHOLD = 25;

const valid = (n) => Number.isFinite(n) && n > 0 && n <= DEFAULT_OWNERSHIP_THRESHOLD;

// The firm's setting from its risk configuration.
export function firmOwnershipThreshold(riskConfig) {
  const n = Number(riskConfig && riskConfig.uboThreshold);
  return valid(n) ? n : DEFAULT_OWNERSHIP_THRESHOLD;
}

// v1_832: the threshold for this case's group. Section 3's per type values
// (risk_config.uboThresholdByType: private_company, regulated_firm,
// partnership, trust) apply by the legal form group first (partnership, trust),
// then the entity type group (private company, regulated firm), then the main
// value. Each is above 0 and no more than 25; anything else is ignored.
export function firmOwnershipThresholdFor(riskConfig, { legalGroup = null, entityGroup = null } = {}) {
  const by = (riskConfig && riskConfig.uboThresholdByType && typeof riskConfig.uboThresholdByType === 'object') ? riskConfig.uboThresholdByType : {};
  for (const g of [legalGroup, entityGroup]) {
    if (!g) continue;
    const n = Number(by[g]);
    if (valid(n)) return { threshold: n, basis: g === legalGroup ? 'legal form group' : 'entity type group', group: g };
  }
  return { threshold: firmOwnershipThreshold(riskConfig), basis: 'main value', group: null };
}

// The threshold a case was built under (its stamp), for screens and exports.
export function caseOwnershipThreshold(report) {
  const n = Number(report && report.ownershipThreshold);
  return valid(n) ? n : DEFAULT_OWNERSHIP_THRESHOLD;
}

// A holding as a number: "15", "15%", "15.5 percent", "25-50%" (lower bound).
export function holdingPercent(v) {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const m = String(v).match(/[\d.]+/);
  if (!m) return null;
  const n = parseFloat(m[0]);
  return Number.isFinite(n) ? n : null;
}

// At or above the threshold (decision, 26/09/26: one rule everywhere).
export function atOrAboveThreshold(pct, threshold) {
  const p = typeof pct === 'number' ? pct : holdingPercent(pct);
  return p !== null && p >= threshold;
}

export const thresholdStatement = (t) => `at or above ${t} percent`;

// The line added to the firm's instructions to the model when the firm sets a
// threshold below the regulatory 25 percent; the regime text stays as written.
export function thresholdPromptLine(t) {
  return t < DEFAULT_OWNERSHIP_THRESHOLD
    ? `This firm identifies beneficial owners at or above ${t} percent (its own policy, stricter than the regulatory 25 percent). Treat every person holding ${t} percent or more as a beneficial owner.`
    : '';
}
export function withThresholdLine(override, t) {
  const line = thresholdPromptLine(t);
  if (!line) return override || '';
  return (override ? override + '\n\n' : '') + line;
}
